/**
 * The in-memory catalogue, as an external store.
 *
 * Not Redux: 3,000 variants in the store would be serialised, diffed and re-selected on every
 * keystroke's re-render, and the index below is a derived structure Redux has no use for. It is
 * an external system with its own lifecycle, which is exactly what `useSyncExternalStore` is for
 * — and it means search results are computed during render, not mirrored into state by an effect.
 */

import type { CatalogueVariant, ShopId, VariantId } from '@pos/shared';

import { adjustCachedQuantity, applySync, readAllVariants, readMeta, replaceAll, type SyncMeta } from './db.js';
import { buildIndex, type CatalogueIndex } from './search.js';

export type CatalogueState = {
  readonly status: 'empty' | 'loading' | 'ready' | 'failed';
  readonly index: CatalogueIndex;
  readonly meta: SyncMeta | null;
  readonly error: string | null;
};

const EMPTY_INDEX: CatalogueIndex = { entries: [], byBarcode: new Map(), size: 0 };

let state: CatalogueState = { status: 'empty', index: EMPTY_INDEX, meta: null, error: null };

const listeners = new Set<() => void>();

function set(next: Partial<CatalogueState>): void {
  state = { ...state, ...next };
  for (const listener of listeners) listener();
}

export function subscribeToCatalogue(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export const getCatalogueSnapshot = (): CatalogueState => state;

let loading: Promise<void> | null = null;

/** Reads IndexedDB and builds the index. Safe to call from anywhere; only the first one works. */
export function loadCatalogue(): Promise<void> {
  loading ??= (async () => {
    set({ status: 'loading', error: null });

    try {
      const [variants, meta] = await Promise.all([readAllVariants(), readMeta()]);
      set({
        status: variants.length > 0 ? 'ready' : 'empty',
        index: buildIndex(variants),
        meta,
        error: null,
      });
    } catch (error) {
      // A failed cache is recoverable — the app still works, it just has to sync before it can
      // search. Saying so is more useful than a spinner that never resolves.
      set({
        status: 'failed',
        error:
          error instanceof Error
            ? `The offline catalogue could not be opened: ${error.message}. Sign out and back in to rebuild it.`
            : 'The offline catalogue could not be opened. Sign out and back in to rebuild it.',
      });
    } finally {
      loading = null;
    }
  })();

  return loading;
}

/** Applies a sync result to both IndexedDB and the in-memory index. */
export async function commitSync(
  variants: readonly CatalogueVariant[],
  removedIds: readonly VariantId[],
  meta: SyncMeta,
  isFullSnapshot: boolean,
): Promise<void> {
  if (isFullSnapshot) await replaceAll(variants, meta);
  else await applySync(variants, removedIds, meta);

  // Rebuilt from the database rather than patched in memory: the two must not be able to
  // disagree, and a rebuild at this size costs 25 ms once a day.
  const all = await readAllVariants();
  set({ status: all.length > 0 ? 'ready' : 'empty', index: buildIndex(all), meta, error: null });
}

/**
 * Decrements what a completed sale sold, so the next search shows the new figure immediately.
 * The server owns stock; this keeps the till from telling the customer there are three left
 * after selling the last one.
 */
export async function applyLocalStockChange(
  deltas: readonly { variantId: VariantId; quantity: number }[],
): Promise<void> {
  await adjustCachedQuantity(deltas);

  const all = await readAllVariants();
  set({ index: buildIndex(all) });
}

/** True when the cached catalogue belongs to a different shop and must be rebuilt from scratch. */
export const isForShop = (shopId: ShopId): boolean => state.meta?.shopId === shopId;

/** Test and boot hook: drops the in-memory copy without touching IndexedDB. */
export function resetCatalogueMemory(): void {
  state = { status: 'empty', index: EMPTY_INDEX, meta: null, error: null };
  for (const listener of listeners) listener();
}
